import React, { useState } from "react";

import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import * as XLSX from "xlsx";
import Cookies from "js-cookie";
import Swal from "sweetalert2";

import { myApi } from "../../../config/axiosConfig";

const normalizeHeader = (value) => {
    return String(value || "")
        .trim()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toUpperCase();
};

const normalizeText = (value) => {
    return String(value ?? "")
        .trim()
        .toUpperCase();
};

const parseNumber = (value) => {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return null;
    }

    let text = String(value)
        .trim()
        .replace(/\s/g, "");

    if (text.includes(",") && text.includes(".")) {
        text = text.replace(/,/g, "");
    } else if (text.includes(",")) {
        text = text.replace(",", ".");
    }

    const number = Number(text);

    return Number.isFinite(number)
        ? number
        : null;
};

const parseDate = (value) => {
    const text = String(value || "").trim();

    const match = text.match(
        /^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/
    );

    if (!match) {
        return null;
    }

    let [, day, month, year] = match;

    if (year.length === 2) {
        year = `20${year}`;
    }

    day = day.padStart(2, "0");
    month = month.padStart(2, "0");

    const result = `${year}-${month}-${day}`;

    const date = new Date(`${result}T00:00:00Z`);

    if (
        date.getUTCFullYear() !== Number(year) ||
        date.getUTCMonth() + 1 !== Number(month) ||
        date.getUTCDate() !== Number(day)
    ) {
        return null;
    }

    return result;
};

const parseTime = (value) => {
    const text = String(value || "").trim();

    const match = text.match(
        /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/
    );

    if (!match) {
        return null;
    }

    const hour = Number(match[1]);
    const minute = Number(match[2]);
    const second = Number(match[3] || 0);

    if (
        hour < 0 ||
        hour > 23 ||
        minute < 0 ||
        minute > 59 ||
        second < 0 ||
        second > 59
    ) {
        return null;
    }

    return [
        String(hour).padStart(2, "0"),
        String(minute).padStart(2, "0"),
        String(second).padStart(2, "0")
    ].join(":");
};

const DefDispensesImportModal = ({
    open,
    onClose,
    onImported
}) => {
    const [file, setFile] = useState(null);
    const [rows, setRows] = useState([]);
    const [errors, setErrors] = useState([]);
    const [reading, setReading] = useState(false);
    const [importing, setImporting] = useState(false);

    const resetModal = () => {
        setFile(null);
        setRows([]);
        setErrors([]);
        setReading(false);
        setImporting(false);
    };

    const handleClose = () => {
        if (reading || importing) {
            return;
        }

        resetModal();
        onClose();
    };

    const handleFile = async (event) => {
        const selectedFile = event.target.files?.[0];

        event.target.value = "";

        if (!selectedFile) {
            return;
        }

        const extension = selectedFile.name
            .split(".")
            .pop()
            ?.toLowerCase();

        if (!["xlsx", "xls"].includes(extension)) {
            Swal.fire({
                icon: "error",
                title: "Archivo inválido",
                text: "Selecciona un archivo Excel .xlsx o .xls"
            });

            return;
        }

        if (selectedFile.size > 10 * 1024 * 1024) {
            Swal.fire({
                icon: "error",
                title: "Archivo demasiado grande",
                text: "El archivo no debe superar 10 MB"
            });

            return;
        }

        try {
            setReading(true);
            setFile(selectedFile);
            setRows([]);
            setErrors([]);

            const arrayBuffer =
                await selectedFile.arrayBuffer();

            const workbook = XLSX.read(arrayBuffer, {
                type: "array"
            });

            const firstSheetName = workbook.SheetNames[0];

            if (!firstSheetName) {
                throw new Error(
                    "El archivo no contiene hojas"
                );
            }

            const sheet =
                workbook.Sheets[firstSheetName];

            const matrix = XLSX.utils.sheet_to_json(
                sheet,
                {
                    header: 1,
                    raw: false,
                    defval: ""
                }
            );

            if (matrix.length < 2) {
                throw new Error(
                    "El archivo no contiene registros"
                );
            }

            const headers = matrix[0].map(
                normalizeHeader
            );

            const columnIndex = {
                fecha: headers.indexOf("FECHA"),
                hora: headers.indexOf("HORA"),
                dispensador: headers.indexOf("UNIDAD"),
                producto: headers.indexOf("FLUIDO"),
                division: headers.indexOf("CONDUCTOR"),
                unidad: headers.indexOf("NUMERO REG."),
                odometro: headers.indexOf("ODOMETRO"),
                cantidad: headers.indexOf("CANTIDAD (L)")
            };

            const requiredColumns = [
                ["FECHA", columnIndex.fecha],
                ["HORA", columnIndex.hora],
                ["FLUIDO", columnIndex.producto],
                ["CONDUCTOR", columnIndex.division],
                ["NÚMERO REG.", columnIndex.unidad],
                ["CANTIDAD (L)", columnIndex.cantidad]
            ];

            const missingColumns = requiredColumns
                .filter(([, index]) => index < 0)
                .map(([name]) => name);

            if (missingColumns.length > 0) {
                throw new Error(
                    `Faltan columnas: ${missingColumns.join(", ")}`
                );
            }

            const parsedRows = [];
            const parsedErrors = [];

            matrix.slice(1).forEach(
                (excelRow, rowIndex) => {
                    const excelRowNumber = rowIndex + 2;

                    const isEmpty = excelRow.every(
                        (cell) =>
                            String(cell ?? "").trim() === ""
                    );

                    if (isEmpty) {
                        return;
                    }

                    const date = parseDate(
                        excelRow[columnIndex.fecha]
                    );

                    const time = parseTime(
                        excelRow[columnIndex.hora]
                    );

                    const producto = normalizeText(
                        excelRow[columnIndex.producto]
                    );

                    const division = normalizeText(
                        excelRow[columnIndex.division]
                    );

                    const unidad = normalizeText(
                        excelRow[columnIndex.unidad]
                    );

                    const cantidadLitros = parseNumber(
                        excelRow[columnIndex.cantidad]
                    );

                    const dispensador =
                        columnIndex.dispensador >= 0
                            ? normalizeText(
                                excelRow[
                                    columnIndex.dispensador
                                ]
                            )
                            : "";

                    const odometro =
                        columnIndex.odometro >= 0
                            ? parseNumber(
                                excelRow[
                                    columnIndex.odometro
                                ]
                            )
                            : null;

                    const rowErrors = [];

                    if (!date) {
                        rowErrors.push("Fecha inválida");
                    }

                    if (!time) {
                        rowErrors.push("Hora inválida");
                    }

                    if (producto !== "DEF") {
                        rowErrors.push(
                            "El fluido debe ser DEF"
                        );
                    }

                    if (!division) {
                        rowErrors.push(
                            "Conductor/división vacío"
                        );
                    }

                    if (!unidad) {
                        rowErrors.push("Unidad vacía");
                    }

                    if (
                        cantidadLitros === null ||
                        cantidadLitros <= 0
                    ) {
                        rowErrors.push(
                            "Cantidad inválida"
                        );
                    }

                    if (rowErrors.length > 0) {
                        parsedErrors.push({
                            fila: excelRowNumber,
                            errores: rowErrors
                        });

                        return;
                    }

                    parsedRows.push({
                        fechaHora: `${date} ${time}`,
                        producto,
                        division,
                        unidad,
                        cantidadLitros,
                        dispensador:
                            dispensador || null,
                        odometro,
                        filaOrigen: excelRowNumber
                    });
                }
            );

            setRows(parsedRows);
            setErrors(parsedErrors);
        } catch (error) {
            console.error(
                "Error leyendo Excel DEF:",
                error
            );

            setFile(null);
            setRows([]);
            setErrors([]);

            Swal.fire({
                icon: "error",
                title: "No fue posible leer el Excel",
                text:
                    error.message ||
                    "Revisa el formato del archivo"
            });
        } finally {
            setReading(false);
        }
    };

    const handleImport = async () => {
        if (!file || rows.length === 0) {
            return;
        }

        try {
            setImporting(true);

            const response = await myApi.post(
                "/def/dispenses/import",
                {
                    archivo: file.name,
                    usuario:
                        Cookies.get("ebt_nombre") ||
                        Cookies.get("ebt_id_user") ||
                        null,
                    registros: rows
                }
            );

            if (!response.data.success) {
                throw new Error(
                    response.data.message ||
                    "No se pudo importar el archivo"
                );
            }

            const result = response.data.data;

            resetModal();
            onClose();

            if (onImported) {
                await onImported();
            }

            await Swal.fire({
                icon: "success",
                title: "Importación terminada",
                html: `
                    <div style="text-align:left">
                        <p>Recibidos: <b>${result.recibidos}</b></p>
                        <p>Insertados: <b>${result.insertados}</b></p>
                        <p>Duplicados: <b>${result.duplicados}</b></p>
                        <p>Rechazados: <b>${result.rechazados}</b></p>
                    </div>
                `
            });
        } catch (error) {
            console.error(
                "Error importando cargas DEF:",
                error
            );

            Swal.fire({
                icon: "error",
                title: "Error de importación",
                text:
                    error.response?.data?.message ||
                    error.message ||
                    "No se pudo importar el archivo"
            });
        } finally {
            setImporting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="lg"
        >
            <DialogTitle>
                Importar cargas de DEF
            </DialogTitle>

            <DialogContent dividers>
                <Box
                    sx={{
                        display: "flex",
                        gap: 2,
                        alignItems: "center",
                        mb: 2
                    }}
                >
                    <Button
                        component="label"
                        variant="contained"
                        startIcon={<UploadFileIcon />}
                        disabled={reading || importing}
                    >
                        Seleccionar Excel

                        <input
                            hidden
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleFile}
                        />
                    </Button>

                    <Typography>
                        {file
                            ? file.name
                            : "Ningún archivo seleccionado"}
                    </Typography>
                </Box>

                {(reading || importing) && (
                    <LinearProgress sx={{ mb: 2 }} />
                )}

                {file && !reading && (
                    <Alert
                        severity={
                            errors.length > 0
                                ? "warning"
                                : "success"
                        }
                        sx={{ mb: 2 }}
                    >
                        Filas válidas: {rows.length}.
                        Filas rechazadas: {errors.length}.
                    </Alert>
                )}

                {errors.length > 0 && (
                    <Alert
                        severity="error"
                        sx={{ mb: 2 }}
                    >
                        {errors.slice(0, 10).map(
                            (error) => (
                                <div key={error.fila}>
                                    Fila {error.fila}:{" "}
                                    {error.errores.join(", ")}
                                </div>
                            )
                        )}

                        {errors.length > 10 && (
                            <div>
                                Y {errors.length - 10} errores más.
                            </div>
                        )}
                    </Alert>
                )}

                {rows.length > 0 && (
                    <>
                        <Typography
                            variant="h6"
                            sx={{ mb: 1 }}
                        >
                            Vista previa
                        </Typography>

                        <TableContainer
                            sx={{
                                maxHeight: 420,
                                border: "1px solid #ddd"
                            }}
                        >
                            <Table
                                stickyHeader
                                size="small"
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Fila</TableCell>
                                        <TableCell>
                                            Fecha y hora
                                        </TableCell>
                                        <TableCell>
                                            División
                                        </TableCell>
                                        <TableCell>
                                            Unidad
                                        </TableCell>
                                        <TableCell align="right">
                                            Cantidad (L)
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {rows.slice(0, 50).map(
                                        (row) => (
                                            <TableRow
                                                key={
                                                    row.filaOrigen
                                                }
                                            >
                                                <TableCell>
                                                    {
                                                        row.filaOrigen
                                                    }
                                                </TableCell>

                                                <TableCell>
                                                    {
                                                        row.fechaHora
                                                    }
                                                </TableCell>

                                                <TableCell>
                                                    {row.division}
                                                </TableCell>

                                                <TableCell>
                                                    {row.unidad}
                                                </TableCell>

                                                <TableCell align="right">
                                                    {Number(
                                                        row.cantidadLitros
                                                    ).toFixed(2)}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {rows.length > 50 && (
                            <Typography
                                variant="caption"
                                sx={{
                                    display: "block",
                                    mt: 1
                                }}
                            >
                                Se muestran las primeras 50 filas
                                de {rows.length}.
                            </Typography>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={handleClose}
                    disabled={reading || importing}
                >
                    Cancelar
                </Button>

                <Button
                    variant="contained"
                    onClick={handleImport}
                    disabled={
                        reading ||
                        importing ||
                        rows.length === 0
                    }
                >
                    {importing
                        ? "Importando..."
                        : `Importar ${rows.length} filas`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DefDispensesImportModal;